import classNames from 'classnames';
import type { ComponentType, PropsWithChildren, ReactNode } from 'react';
import { FormattedDate } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled, { css } from 'styled-components';

import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelL } from '#src/atoms/typography/index.tsx';

const TimelineRowWrapper = styled(Horizontal)`
  border-radius: var(--unit);
  align-items: flex-start;
  position: relative;
`;

const Dot = styled(Horizontal).attrs({ gap: 0, center: true })<{
  completed?: boolean;
}>`
  width: var(--dot-width, var(--quadruple-unit));
  height: var(--dot-width, var(--quadruple-unit));
  border-radius: 50%;
  box-sizing: border-box;
  z-index: 1;
  ${({ completed }) =>
    completed
      ? css`
          background: var(--progress-color, var(--c-score-high));
          color: var(--c-black);
        `
      : css`
          background-color: var(--c-nd-100-opaque);
          color: var(--c-white);
        `}
`;

const VerticalLine = styled.div`
  --line-width: 10px;
  width: var(--line-width);
  background: linear-gradient(
    to bottom,
    var(--progress-color, var(--c-score-high)) var(--progress),
    var(--c-nd-150) var(--progress),
    var(--c-nd-150) 100%
  );
  min-height: var(--quadruple-unit);
  margin-bottom: calc(-1 * var(--intermediate-unit));
  position: absolute;
  top: calc(var(--dot-width, var(--quadruple-unit)) - 1px);
  left: calc(
    var(--dot-width, var(--quadruple-unit)) / 2 - var(--line-width) / 2
  );
  z-index: -1;
  bottom: 0;
`;

const RowContent = styled(Vertical)`
  padding: var(--half-unit) 0 var(--quadruple-unit) 0;
  flex: 1;
  &.isLastRow {
    padding-bottom: 0;
  }
`;

type Props = {
  label?: ReactNode;
  date?: Date;
  progress: number;
  isLastRow?: boolean;
  dot?: (
    Dot: ComponentType<PropsWithChildren<{ completed?: boolean }>>
  ) => ReactNode;
  icon?: ReactNode;
};
export const TimelineRow = ({
  label,
  progress,
  children,
  date,
  isLastRow,
  dot,
  icon,
}: PropsWithChildren<Props>) => {
  return (
    <TimelineRowWrapper>
      {!isLastRow && (
        <VerticalLine
          style={
            {
              '--progress': `${progress * 100}%`,
            } as CSSProperties
          }
        />
      )}
      {dot?.(Dot) || <Dot completed={progress > 0}>{icon}</Dot>}
      <RowContent className={classNames({ isLastRow })}>
        <LabelL bold color={progress > 0 ? 'var(--c-white)' : 'currentColor'}>
          {label || (
            <FormattedDate
              value={date}
              day="numeric"
              month="short"
              weekday="long"
            />
          )}
        </LabelL>
        <div>{children}</div>
      </RowContent>
    </TimelineRowWrapper>
  );
};
